import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import EntityPlusMoreComponent from './EntityPlusMoreComponent.js';
import BadgeComponent from './BadgeComponent.js';
import TableCellTextWithIcon from '../TableCellTextWithIcon/TableCellTextWithIcon.js';

// 🔑 shared truncation map
const truncationMap = new Map();

// icon width used as offset before ForgeIcon loads from CDN
const ICON_OFFSET = 24;

// helper to build a unique key for each cell
const makeKey = (node, column) => {
  const rowId = node?.id ?? node?.rowIndex ?? '';
  const colId =
    typeof column?.getId === 'function' ? column.getId() : column?.colId ?? '';
  return `${rowId}|${colId}`;
};

// named export for AG Grid tooltip system
export const tooltipValueGetter = (params) => {
  const key = makeKey(params.node, params.column);
  return truncationMap.get(key) ? params.valueFormatted ? params.valueFormatted : params.value : null;
};

const TruncatedCellRenderer = (props) => {
  const { value, node, column, api, main, number, toggleId, type, theme, icon } = props;
  const [initial, setInitial] = useState(true);
  const ref = useRef(null);
  const key = makeKey(node, column);
  const [isTruncated, setIsTruncated] = React.useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const checkTruncation = () => {
    //         _____     ____
    //        /      \  |  o | 
    //       |        |/ ___\| 
    //       |_________/     
    //       |_|_| |_|_|
    
      if (el.querySelector('forge-badge')) {
        //special case for ForgeBadge
        const badgeElement = el.querySelector('forge-badge');
        const shadowRootContentElement =
          badgeElement.shadowRoot?.querySelector('[part="content"]');
        if (shadowRootContentElement) {
          const isTruncated =
            shadowRootContentElement.scrollWidth >
            shadowRootContentElement.clientWidth;
          truncationMap.set(key, isTruncated);
          return;
        }
      }

      let isTruncated;
      if (initial) {
        isTruncated = el.scrollWidth + ICON_OFFSET > el.clientWidth;
        setInitial(false);
      } else {
        isTruncated = el.scrollWidth > el.clientWidth;
      }

      truncationMap.set(key, isTruncated);
      setIsTruncated(isTruncated);
    };

    checkTruncation();

    if (api) {
      api.refreshCells({ rowNodes: [node], columns: [column], force: true });
    }

    const ro = new ResizeObserver(() => {
      checkTruncation();
      if (api) {
        api.refreshCells({ rowNodes: [node], columns: [column], force: true });
      }
    });
    ro.observe(el);

    return () => {
      ro.disconnect();
      truncationMap.delete(key);
    };
  }, [value, key, api, node, column, isTruncated]);

  const renderComponent = () => {
    switch (type) {
      case 'badge':
        return <BadgeComponent main={main} theme={theme} />;
      case 'textWithIcon':
        return <TableCellTextWithIcon icon={icon} text={value} />;
      default:
        return (
          <EntityPlusMoreComponent
            key={String(isTruncated)} 
            main={main}
            number={number}
            toggleId={toggleId}
            isTruncated={isTruncated}
            value={value}
          />
        );
    }
  };

  return (
    <div
      ref={ref}
      style={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {/* Empty div to prevent double tooltip issue on Safari browser */}
      <div></div>
      {renderComponent()}
    </div>
  );
};

TruncatedCellRenderer.propTypes = {
  component: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  value: PropTypes.any,
  node: PropTypes.any,
  column: PropTypes.any,
  api: PropTypes.any,
  main: PropTypes.string,
  number: PropTypes.number,
  toggleId: PropTypes.string,
  type: PropTypes.string,
  theme: PropTypes.string,
  icon: PropTypes.string,
};

export default TruncatedCellRenderer;
